<script lang="ts" setup>
const emit = defineEmits<{
  (event: "update:value", value: number | null): void;
  (event: "reset", value: number | null): void;
  (event: "minimize", value: number): void;
  (event: "maximize", value: number): void;
  (event: "decrease", value: number, steps: number): void;
  (event: "increase", value: number, steps: number): void;
}>();

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    value: number | null | undefined;
    default?: number | null;
    min?: number;
    max?: number;
    step?: number;
  }>(),
  {
    disabled: false,
    value: undefined,
    default: null,
    min: 1,
    max: 6.5,
    step: 0.5,
  }
);

const internalValue = ref(typeof props.value !== "undefined" ? props.value : null);
const value = computed({
  get: () => (typeof props.value !== "undefined" ? props.value : internalValue.value),
  set: (v: number | null) => {
    if (v !== null) {
      if (v < props.min) v = props.min;
      else if (v > props.max) v = props.max;
    }
    emit("update:value", (internalValue.value = v));
  },
});

const update = (v: number | null) => (value.value = v);
const reset = (v?: number | null) => emit("reset", (value.value = typeof v !== "undefined" ? v : props.default));
const minimize = (v?: number) => emit("minimize", (value.value = typeof v !== "undefined" ? v : props.min));
const maximize = (v?: number) => emit("maximize", (value.value = typeof v !== "undefined" ? v : props.max));
const decrease = (steps = 1) => {
  value.value = value.value === null ? props.max - steps * props.step : value.value - steps * props.step;
  emit("decrease", value.value, steps);
};
const increase = (steps = 1) => {
  value.value = value.value === null ? props.min + steps * props.step : value.value + steps * props.step;
  emit("increase", value.value, steps);
};

const values = computed(() => {
  const vs: number[] = [];
  for (let i = props.min; i < props.max + props.step; i += props.step) vs.push(+i.toFixed(5));
  return vs;
});

defineExpose({ value, update, reset, minimize, maximize, increase, decrease });
</script>

<template>
  <ul class="tw-checkbox-list filter--rooms-min">
    <li v-for="n in values" :key="`TwFilter_RoomsMin_Checkbox_${n}`" class="tw-checkbox-list--item">
      <tw-ui-checkbox :disabled="props.disabled" :state="n === props.value" v-bind="$attrs" @check="update(n)">
        {{ $t("filter.rooms_min.value.long", [$n(n, "rooms")]) }}
      </tw-ui-checkbox>
    </li>
  </ul>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/tw-variables.scss";

.tw-checkbox-list {
  display: flex;
  flex-flow: column nowrap;
  gap: $tw-component--gap;
  padding: 0;
  list-style-type: none;
}

.tw-checkbox-list--item {
}

.tw-checkbox {
  cursor: pointer;
}
</style>
